import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';

// Markdown components configuration
const markdownComponents = {
  p: ({ node, ...props }) => (
    <Typography component="span" variant="body1" sx={{ display: 'block', mb: 1 }}>
      {props.children}
    </Typography>
  ),
  a: ({ node, href, ...props }) => {
    const isInternal = href?.startsWith('/');
    if (isInternal) {
      return (
        <RouterLink
          to={href}
          style={{ color: '#1abc9c', textDecoration: 'underline' }}
        >
          {props.children}
        </RouterLink>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1abc9c', textDecoration: 'underline' }}
        {...props}
      />
    );
  },
  li: ({ node, ...props }) => (
    <Typography component="li" variant="body1" sx={{ ml: 2 }}>
      {props.children}
    </Typography>
  ),
  ul: ({ node, ...props }) => (
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {props.children}
    </Box>
  ),
  ol: ({ node, ...props }) => (
    <Box component="ol" sx={{ m: 0, pl: 2 }}>
      {props.children}
    </Box>
  ),
  strong: ({ node, ...props }) => (
    <Box component="strong" sx={{ fontWeight: 'bold' }}>
      {props.children}
    </Box>
  ),
};

const RightImage = ({
  image,
  mainTitle = 'Jopa Karasya',
  subHeader1 = 'Question',
  subHeader2,
  text1 = '- Are years of valuable data stuck in silos?\n\n- Is data slowing you down?\n\n- Are you struggling to get insights from your data?',
  text2,
  hideBorder = false, // new prop
}) => {
  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: '70vh', // adjust as needed
      }}
    >
      {/* Left side: main title + framed content block */}
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: 0, md: 4 },
        }}
      >
        {/* Main Title */}
        <Typography variant="h4" sx={{ mb: 3, mt: 4, px: { xs: 0, md: 4 } }}>
          {mainTitle}
        </Typography>

        {/* Content blocks in separate frames */}
        <Grid container spacing={2} alignItems="stretch">
          {subHeader2 && text2 ? (
            <>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: '100%',
                    border: (theme) =>
                      hideBorder
                        ? 'none'
                        : `${theme.customBorderWeight} solid ${theme.palette.secondary.main}`,
                    borderRadius: (theme) => theme.customRounding.contentFrame,
                    p: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ color: (theme) => theme.palette.text.primary, mb: 2 }}>
                    {subHeader1}
                  </Typography>
                  <Box sx={{ '& > *:last-child': { mb: 0 } }}>
                    <ReactMarkdown components={markdownComponents}>
                      {text1}
                    </ReactMarkdown>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: '100%',
                    border: (theme) =>
                      hideBorder
                        ? 'none'
                        : `${theme.customBorderWeight} solid ${theme.palette.secondary.main}`,
                    borderRadius: (theme) => theme.customRounding.contentFrame,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: (theme) => theme.palette.secondary.main, mb: 2 }}
                  >
                    {subHeader2}
                  </Typography>
                  <Box sx={{ '& > *:last-child': { mb: 0 } }}>
                    <ReactMarkdown components={markdownComponents}>
                      {text2}
                    </ReactMarkdown>
                  </Box>
                </Box>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  height: '100%',
                  border: (theme) =>
                    hideBorder
                      ? 'none'
                      : `${theme.customBorderWeight} solid ${theme.palette.secondary.main}`,
                  borderRadius: (theme) => theme.customRounding.contentFrame,
                  p: 3,
                }}
              >
                <Typography variant="h5" sx={{ color: (theme) => theme.palette.text.primary, mb: 2 }}>
                  {subHeader1}
                </Typography>
                <Box sx={{ '& > *:last-child': { mb: 0 } }}>
                  <ReactMarkdown components={markdownComponents}>
                    {text1}
                  </ReactMarkdown>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Right side: show the entire image */}
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={image}
          alt="Right side"
          sx={{
            width: '100%',   // fill the horizontal space
            height: 'auto',  // maintain aspect ratio
            display: 'block',
            borderRadius: (theme) => theme.customImageRounding,
          }}
        />
      </Box>
    </Box>
  );
};

export default RightImage;